/**
 * Market chip labels and one-line descriptions, keyed by market id.
 *
 * Lives here rather than inside InlineMarketPicker so it can be asserted against the server's
 * market list: `chipLabel` falls back to `name.slice(0, 12)`, and that fallback silently turned
 * "Match Winner (Regulation)" into "Match Winner" — the one label basketball's regulation market
 * must never carry, since it is the only basketball market that ignores overtime.
 *
 * See src/create/__tests__/marketLabels.parity.test.ts.
 */

import {
  MARKET_IDS,
  BASKETBALL_MARKET_IDS,
  FORMULA_ONE_MARKET_IDS,
} from './matchDetails.constants';

/**
 * Chip label per market id. Exported so the parity test can assert that EVERY market the server
 * ships has one — the fallback below truncates to 12 characters, which is how
 * "Match Winner (Regulation)" came to render as "Match Winner".
 */
export const CHIP_LABEL: Partial<Record<number, string>> = {
  // Formula One (specs/formula-one-challenges) — without these the chips fall back to a truncated
  // market name, so "Podium Finish" would read as "Podium Finis".
  [FORMULA_ONE_MARKET_IDS.RACE_WINNER]: 'Winner',
  [FORMULA_ONE_MARKET_IDS.PODIUM_FINISH]: 'Podium',
  [FORMULA_ONE_MARKET_IDS.FASTEST_LAP]: 'Fastest Lap',
  // Basketball (specs/multi-sport-challenges) — without these the chips render unlabelled.
  [BASKETBALL_MARKET_IDS.MONEYLINE]: 'Moneyline',
  [BASKETBALL_MARKET_IDS.POINT_SPREAD]: 'Spread',
  [BASKETBALL_MARKET_IDS.TOTAL_POINTS]: 'Points O/U',
  [BASKETBALL_MARKET_IDS.FIRST_QUARTER_WINNER]: 'Q1 Win',
  [BASKETBALL_MARKET_IDS.HIGHEST_SCORING_QUARTER]: 'Hi Qtr',
  [BASKETBALL_MARKET_IDS.ODD_EVEN]: 'Odd/Even',
  // MUST name the basis. The fallback truncates "Match Winner (Regulation)" to exactly
  // "Match Winner" — which is the one label this market must never carry, since a pick that
  // loses on an overtime result would look like a wrong settlement.
  [BASKETBALL_MARKET_IDS.REGULATION_1X2]: 'Reg. Winner',
  [MARKET_IDS.MATCH_WINNER]: 'Match',
  [MARKET_IDS.DOUBLE_CHANCE]: 'Double',
  [MARKET_IDS.BTTS]: 'BTTS',
  [MARKET_IDS.OVER_UNDER_15]: 'Goals O/U',
  [MARKET_IDS.OVER_UNDER_05_1ST_HALF]: '1H O/U',
  [MARKET_IDS.CORNERS]: 'Corners',
  [MARKET_IDS.CARDS]: 'Cards',
  [MARKET_IDS.CORRECT_SCORE]: 'Score',
  [MARKET_IDS.HALF_TIME_CORRECT_SCORE]: '1H Score',
  [MARKET_IDS.FIRST_HALF_WINNER]: '1H Win',
  [MARKET_IDS.SECOND_HALF_WINNER]: '2H Win',
  [MARKET_IDS.ODD_EVEN]: 'Odd/Even',
  [MARKET_IDS.EXACT_GOALS]: 'Exact Goals',
  [MARKET_IDS.HALF_TIME_FULL_TIME]: 'HT/FT',
  [MARKET_IDS.HIGHEST_SCORING_HALF]: 'Hi Half',
  [MARKET_IDS.HANDICAP_RESULT]: 'Handicap',
  [MARKET_IDS.HANDICAP_MINUS_4]: 'Handicap',
  [MARKET_IDS.CLEAN_SHEET_HOME]: 'CS Home',
  [MARKET_IDS.CLEAN_SHEET_AWAY]: 'CS Away',
  [MARKET_IDS.BTTS_FIRST_HALF]: 'BTTS 1H',
  [MARKET_IDS.BTTS_SECOND_HALF]: 'BTTS 2H',
  [MARKET_IDS.WIN_BOTH_HALVES]: 'Win Both',
  [MARKET_IDS.OVER_UNDER_05_2ND_HALF]: '2H O/U',
};

export const SHORT_MARKET_DESC: Partial<Record<number, string>> = {
  [FORMULA_ONE_MARKET_IDS.RACE_WINNER]: 'Pick the driver who wins the race',
  [FORMULA_ONE_MARKET_IDS.PODIUM_FINISH]: 'Pick a driver to finish in the top 3',
  [FORMULA_ONE_MARKET_IDS.FASTEST_LAP]: 'Pick the driver who sets the fastest lap',
  // Basketball. Regulation 1X2 states the overtime rule outright — it is the whole difference
  // between this market and Moneyline, and the user has to know it before they stake.
  [BASKETBALL_MARKET_IDS.REGULATION_1X2]: 'Result after regulation — overtime does not count',
  [BASKETBALL_MARKET_IDS.MONEYLINE]: 'Pick the winner, overtime included',
  [BASKETBALL_MARKET_IDS.POINT_SPREAD]: 'Pick a side with a points handicap',
  [BASKETBALL_MARKET_IDS.TOTAL_POINTS]: 'Predict total points scored',
  [BASKETBALL_MARKET_IDS.FIRST_QUARTER_WINNER]: 'Who wins the first quarter?',
  [BASKETBALL_MARKET_IDS.HIGHEST_SCORING_QUARTER]: 'Which quarter has the most points?',
  [BASKETBALL_MARKET_IDS.ODD_EVEN]: 'Will total points be odd or even?',
  [MARKET_IDS.MATCH_WINNER]: 'Pick the final match result',
  [MARKET_IDS.DOUBLE_CHANCE]: 'Cover 2 of 3 possible outcomes',
  [MARKET_IDS.BTTS]: 'Will both teams score?',
  [MARKET_IDS.OVER_UNDER_15]: 'Predict total goals scored',
  [MARKET_IDS.OVER_UNDER_05_1ST_HALF]: 'Goals over/under in 1st half',
  [MARKET_IDS.CORNERS]: 'Predict total corner kicks',
  [MARKET_IDS.CORRECT_SCORE]: 'Predict the exact final score',
  [MARKET_IDS.HALF_TIME_CORRECT_SCORE]: 'Predict the exact half-time score',
  [MARKET_IDS.FIRST_HALF_WINNER]: 'Who wins the first half?',
  [MARKET_IDS.SECOND_HALF_WINNER]: 'Who wins the second half?',
  [MARKET_IDS.ODD_EVEN]: 'Will total goals be odd or even?',
  [MARKET_IDS.EXACT_GOALS]: 'Predict exact number of goals',
  [MARKET_IDS.HALF_TIME_FULL_TIME]: 'Pick result at half-time & full-time',
  [MARKET_IDS.HIGHEST_SCORING_HALF]: 'Which half will have more goals?',
  [MARKET_IDS.HANDICAP_RESULT]: 'Bet with a virtual goal advantage',
  [MARKET_IDS.HANDICAP_MINUS_4]: 'Bet with a virtual goal advantage',
  [MARKET_IDS.CLEAN_SHEET_HOME]: 'Will home team keep a clean sheet?',
  [MARKET_IDS.CLEAN_SHEET_AWAY]: 'Will away team keep a clean sheet?',
  [MARKET_IDS.BTTS_FIRST_HALF]: 'Will both teams score in the 1st half?',
  [MARKET_IDS.BTTS_SECOND_HALF]: 'Will both teams score in the 2nd half?',
  [MARKET_IDS.WIN_BOTH_HALVES]: 'Which team wins both halves?',
  [MARKET_IDS.OVER_UNDER_05_2ND_HALF]: 'Goals over/under in 2nd half',
  [MARKET_IDS.OVER_UNDER_15_2ND_HALF]: 'Goals over/under in 2nd half',
  [MARKET_IDS.OVER_UNDER_25_2ND_HALF]: 'Goals over/under in 2nd half',
};
